import { useCallback } from 'react';

export function useBelotRoundLifecycle({
  createInitialState,
  applyCommand,
  apply,
  afterEngineCommandApplied,
  targetScore,
  declarationComboMode,
  /** Режим шулера в начальном состоянии движка (локальная отладка). */
  shulerEnabled = false,
  setSessionStarted,
  setBanner,
  setScoreSheetRows,
  setScoreSheetOpen,
  setLastRoundSummary,
  setEngineDebugOpen,
  setEngineDebugLog,
  setPeekLastTrickOpen,
  clearLastTrickLongPress,
  setEngineState,
  setSeatArrangeMode,
  setTrickAnim,
  setLastTrickSnapshot,
  setBellaNextPlay,
  prevHeroTimedTurnRef,
  prevTrickLenRef,
  lastFourTrickRef,
  dealAnimGenRef,
  dealFlightAbortRef,
  setRoundDealAnimActive,
  setSeatDealSlotRevealed,
  pendingPlayCmdRef,
  flyTweenRef,
  trickRevealToCollectRef,
  trickCollectTlRef,
  setCardFly,
  play,
}) {
  const startSession = useCallback(() => {
    setBanner('');
    setScoreSheetRows([]);
    setScoreSheetOpen(false);
    setLastRoundSummary(null);
    setEngineDebugLog([]);
    const base = createInitialState({
      targetScore,
      rng: Math.random,
      declarationMode: declarationComboMode,
      shulerEnabled,
    });
    try {
      const { state, events } = applyCommand(base, { type: 'START_ROUND' });
      setEngineState(state);
      afterEngineCommandApplied({ type: 'START_ROUND' }, base, state, events);
      setSessionStarted(true);
      play('belot-shuffle-deal', 0.6);
      play('notification');
    } catch (e) {
      console.error(e);
      setBanner(e instanceof Error ? e.message : String(e));
    }
  }, [
    setBanner,
    setScoreSheetRows,
    setScoreSheetOpen,
    setLastRoundSummary,
    setEngineDebugLog,
    createInitialState,
    targetScore,
    declarationComboMode,
    shulerEnabled,
    applyCommand,
    setEngineState,
    afterEngineCommandApplied,
    setSessionStarted,
    play,
  ]);

  const nextRound = useCallback(() => {
    dealAnimGenRef.current += 1;
    dealFlightAbortRef.current = true;
    setRoundDealAnimActive(false);
    setSeatDealSlotRevealed(null);
    pendingPlayCmdRef.current = null;
    flyTweenRef.current?.kill();
    flyTweenRef.current = null;
    trickRevealToCollectRef.current?.kill();
    trickRevealToCollectRef.current = null;
    trickCollectTlRef.current?.kill();
    trickCollectTlRef.current = null;
    setCardFly(null);
    setTrickAnim(null);
    setLastTrickSnapshot(null);
    prevTrickLenRef.current = 0;
    lastFourTrickRef.current = null;
    setBanner('');
    setLastRoundSummary(null);
    play('button-click');
    play('belot-shuffle-deal', 0.55);
    apply({ type: 'START_ROUND' });
  }, [
    dealAnimGenRef,
    dealFlightAbortRef,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    pendingPlayCmdRef,
    flyTweenRef,
    trickRevealToCollectRef,
    trickCollectTlRef,
    setCardFly,
    setTrickAnim,
    setLastTrickSnapshot,
    prevTrickLenRef,
    lastFourTrickRef,
    setBanner,
    setLastRoundSummary,
    play,
    apply,
  ]);

  const resetToMenu = useCallback(() => {
    dealAnimGenRef.current += 1;
    dealFlightAbortRef.current = true;
    setRoundDealAnimActive(false);
    setSeatDealSlotRevealed(null);
    pendingPlayCmdRef.current = null;
    flyTweenRef.current?.kill();
    flyTweenRef.current = null;
    trickRevealToCollectRef.current?.kill();
    trickRevealToCollectRef.current = null;
    trickCollectTlRef.current?.kill();
    trickCollectTlRef.current = null;
    setCardFly(null);
    setSessionStarted(false);
    setScoreSheetRows([]);
    setScoreSheetOpen(false);
    setEngineDebugOpen(false);
    setEngineDebugLog([]);
    setPeekLastTrickOpen(false);
    clearLastTrickLongPress();
    setEngineState(
      createInitialState({
        targetScore,
        rng: Math.random,
        declarationMode: declarationComboMode,
        shulerEnabled,
      }),
    );
    setBanner('');
    setSeatArrangeMode(false);
    setTrickAnim(null);
    setLastTrickSnapshot(null);
    setLastRoundSummary(null);
    setBellaNextPlay(false);
    prevHeroTimedTurnRef.current = false;
    prevTrickLenRef.current = 0;
    lastFourTrickRef.current = null;
  }, [
    dealAnimGenRef,
    dealFlightAbortRef,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    pendingPlayCmdRef,
    flyTweenRef,
    trickRevealToCollectRef,
    trickCollectTlRef,
    setCardFly,
    setSessionStarted,
    setScoreSheetRows,
    setScoreSheetOpen,
    setEngineDebugOpen,
    setEngineDebugLog,
    setPeekLastTrickOpen,
    clearLastTrickLongPress,
    setEngineState,
    createInitialState,
    targetScore,
    declarationComboMode,
    shulerEnabled,
    setBanner,
    setSeatArrangeMode,
    setTrickAnim,
    setLastTrickSnapshot,
    setLastRoundSummary,
    setBellaNextPlay,
    prevHeroTimedTurnRef,
    prevTrickLenRef,
    lastFourTrickRef,
  ]);

  const beginNewMatchAfterEnd = useCallback(() => {
    dealAnimGenRef.current += 1;
    dealFlightAbortRef.current = true;
    setRoundDealAnimActive(false);
    setSeatDealSlotRevealed(null);
    pendingPlayCmdRef.current = null;
    flyTweenRef.current?.kill();
    trickRevealToCollectRef.current?.kill();
    trickRevealToCollectRef.current = null;
    trickCollectTlRef.current?.kill();
    trickCollectTlRef.current = null;
    setCardFly(null);
    play('button-click');
    play('belot-shuffle-deal', 0.55);
    setScoreSheetRows([]);
    setScoreSheetOpen(false);
    setLastRoundSummary(null);
    setLastTrickSnapshot(null);
    setPeekLastTrickOpen(false);
    clearLastTrickLongPress();
    setTrickAnim(null);
    setBellaNextPlay(false);
    prevTrickLenRef.current = 0;
    lastFourTrickRef.current = null;
    apply({ type: 'START_ROUND' });
  }, [
    dealAnimGenRef,
    dealFlightAbortRef,
    setRoundDealAnimActive,
    setSeatDealSlotRevealed,
    pendingPlayCmdRef,
    flyTweenRef,
    trickRevealToCollectRef,
    trickCollectTlRef,
    setCardFly,
    play,
    setScoreSheetRows,
    setScoreSheetOpen,
    setLastRoundSummary,
    setLastTrickSnapshot,
    setPeekLastTrickOpen,
    clearLastTrickLongPress,
    setTrickAnim,
    setBellaNextPlay,
    prevTrickLenRef,
    lastFourTrickRef,
    apply,
  ]);

  return { startSession, nextRound, resetToMenu, beginNewMatchAfterEnd };
}

